import { Controller } from "@hotwired/stimulus"
import { Dropdown } from "bootstrap"

// The AI summary on the results card. The tab opens a menu (a Bootstrap
// dropdown with a lazy Turbo frame, see ai_summary/_tab and ai_summary/index);
// its button streams the summary into the panel above the hits (see
// ai_summary/_panel). Only the user's own summary from the cache starts
// without that click.
export default class extends Controller {
  static targets = [
    "tab", "panel", "heading", "placeholder", "output", "errorOutput", "disclaimer",
    "quota", "generateLabel", "showLabel", "feedback", "feedbackToken"
  ]
  static values = { done: Boolean, errorMessage: String, autostartUrl: String }

  connect() {
    this.buffer = ""
    this.beforeCache = () => this.prepareForCache()
    document.addEventListener("turbo:before-cache", this.beforeCache)

    // The user's own summary of the search, from the cache: shows again right
    // away. Not in Turbo's preview of a cached page, and not again once Turbo
    // restored the page with the summary done.
    if (this.autostartUrlValue && !this.doneValue && !document.documentElement.hasAttribute("data-turbo-preview")) {
      this.panelTarget.hidden = false
      this.startStream(this.autostartUrlValue)
    }
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.beforeCache)
    this.stopStream()
  }

  // Creates the summary or shows a cached one. While the stream runs or once
  // it is done, the button only shows the panel again: no second request.
  generate({ params: { url } }) {
    this.hideMenu()
    this.panelTarget.hidden = false
    // Leads keyboards and screen readers from the closed menu to the summary
    if (this.hasHeadingTarget) this.headingTarget.focus()

    if (this.doneValue || this.source) return

    this.startStream(url)
  }

  // Hides the panel. A summary stays on the page and shows again for free.
  close() {
    this.panelTarget.hidden = true
  }

  hideMenu() {
    if (this.hasTabTarget) Dropdown.getOrCreateInstance(this.tabTarget).hide()
  }

  startStream(url) {
    this.buffer = ""
    this.outputTarget.innerHTML = ""
    this.errorOutputTarget.hidden = true
    this.placeholderTarget.hidden = false
    if (this.hasQuotaTarget) this.quotaTarget.hidden = true

    this.source = new EventSource(new URL(url, window.location.origin).toString())

    this.source.onmessage = (event) => {
      this.placeholderTarget.hidden = true
      this.disclaimerTarget.hidden = false
      this.buffer += event.data.replace(/\\n/g, "\n")
      this.outputTarget.innerHTML = this.buffer
    }

    this.source.addEventListener("done", (event) => {
      // An empty answer: nothing to show, nothing to rate. A new click tries again.
      if (!this.buffer) {
        this.showError(this.errorMessageValue)
        return
      }

      this.stopStream()
      // Kept as an attribute in the cached page of Turbo (see prepareForCache)
      this.doneValue = true
      this.showDoneLabel()
      this.showFeedback(event.data)
    })

    // Errors of the server carry a message. Errors of the connection carry none
    // (e.g. the network, an expired session, the feature switched off) and get
    // the general one. The first error stops the stream, later ones are ignored.
    this.source.addEventListener("error", (event) => {
      if (!this.source) return

      this.showError(event.data ? event.data.replace(/\\n/g, "\n") : this.errorMessageValue)
    })
  }

  showError(message) {
    this.stopStream()
    this.placeholderTarget.hidden = true
    this.disclaimerTarget.hidden = true
    this.outputTarget.innerHTML = ""
    this.errorOutputTarget.innerHTML = message
    this.errorOutputTarget.hidden = false
  }

  stopStream() {
    this.source?.close()
    this.source = null
  }

  // After a summary, the button of the menu shows it again instead of creating one.
  showDoneLabel() {
    if (this.hasGenerateLabelTarget) this.generateLabelTarget.hidden = true
    if (this.hasShowLabelTarget) this.showLabelTarget.hidden = false
  }

  // The done event carries the token to rate the stored summary (see
  // ai_summary_feedback_controller.js). Without a token there is nothing to rate.
  showFeedback(data) {
    if (!data || !this.hasFeedbackTarget || !this.hasFeedbackTokenTarget) return

    const { feedback_token: token } = JSON.parse(data)
    if (!token) return

    this.feedbackTokenTarget.value = token
    this.feedbackTarget.hidden = false
  }

  // Turbo keeps the page for the back button. The menu is closed in that copy,
  // and a summary still streaming is reset: its stream ends with the page, and
  // the copy must not show a spinner that never stops. A summary that is done
  // stays and isn't requested again.
  prepareForCache() {
    this.hideMenu()

    if (!this.source || this.doneValue) return

    this.stopStream()
    this.panelTarget.hidden = true
    this.placeholderTarget.hidden = true
    this.disclaimerTarget.hidden = true
    this.outputTarget.innerHTML = ""
    if (this.hasQuotaTarget) this.quotaTarget.hidden = false
  }
}
